import React, { useEffect, useState, useCallback, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Switch } from '@/components/ui/switch';
import { Briefcase, RefreshCw, RotateCw, Loader2, AlertTriangle, CheckCircle2, Clock, PlayCircle, Radio, ExternalLink, Search as SearchIcon, Bell, Activity } from 'lucide-react';
import { toast } from 'sonner';
import { api } from '@/lib/api';

const STATUS_STYLES = {
  queued: { color: 'bg-slate-500/15 text-slate-300 border-slate-500/30', icon: Clock },
  running: { color: 'bg-blue-500/15 text-blue-300 border-blue-500/30', icon: PlayCircle },
  done: { color: 'bg-emerald-500/15 text-emerald-300 border-emerald-500/30', icon: CheckCircle2 },
  failed: { color: 'bg-rose-500/15 text-rose-300 border-rose-500/30', icon: AlertTriangle },
};

function StatusBadge({ status }) {
  const cfg = STATUS_STYLES[status] || STATUS_STYLES.queued;
  const Icon = cfg.icon;
  return (
    <span className={`inline-flex items-center gap-1 px-2 py-0.5 rounded-md border text-[10px] font-medium ${cfg.color}`} data-testid={`job-status-${status}`}>
      <Icon className="w-3 h-3" />{status}
    </span>
  );
}

function fmtDuration(ms) {
  if (ms == null) return '—';
  if (ms < 1000) return `${ms}ms`;
  if (ms < 60_000) return `${(ms / 1000).toFixed(1)}s`;
  return `${Math.floor(ms / 60000)}m ${Math.round((ms % 60000) / 1000)}s`;
}

function fmtTime(iso) {
  if (!iso) return '—';
  try { return new Date(iso).toLocaleString(); } catch { return iso; }
}

/**
 * Resolve a deep-link target for a job based on its kind + payload.
 * Returns `{ to, label, icon }` or null if the kind has no known target.
 */
function getJobDrillTarget(job) {
  const payload = job.payload || {};
  switch (job.kind) {
    case 'schedule.execute': {
      const sid = payload.schedule_id;
      return { to: sid ? `/scheduling?schedule=${encodeURIComponent(sid)}` : '/scheduling', label: 'View schedule', icon: Clock };
    }
    case 'anomaly.scan': {
      const scanId = payload.scan_id;
      return { to: scanId ? `/anomaly-scans?scan=${encodeURIComponent(scanId)}` : '/anomaly-scans', label: 'View scan', icon: SearchIcon };
    }
    case 'alert.evaluate': {
      const alertId = payload.alert_id;
      return { to: alertId ? `/alerts?alert=${encodeURIComponent(alertId)}` : '/alerts', label: 'View alert', icon: Bell };
    }
    case 'embed.refresh':
      return { to: '/embed-manager', label: 'Embed manager', icon: Activity };
    default:
      return null;
  }
}

export default function AdminJobsPage() {
  const navigate = useNavigate();
  const [jobs, setJobs] = useState([]);
  const [loading, setLoading] = useState(true);
  const [statusFilter, setStatusFilter] = useState('all');
  const [kindFilter, setKindFilter] = useState('all');
  const [retrying, setRetrying] = useState({});
  const [expanded, setExpanded] = useState({});
  const [liveTail, setLiveTail] = useState(false);
  const liveTailRef = useRef(null);

  const loadJobs = useCallback(async () => {
    setLoading(true);
    try {
      const params = new URLSearchParams({ limit: '100' });
      if (statusFilter !== 'all') params.append('status', statusFilter);
      if (kindFilter !== 'all') params.append('kind', kindFilter);
      const data = await api.listJobs(params.toString());
      setJobs(Array.isArray(data) ? data : []);
    } catch (err) {
      toast.error(err.message || 'Could not load jobs');
    } finally {
      setLoading(false);
    }
  }, [statusFilter, kindFilter]);

  useEffect(() => { loadJobs(); }, [loadJobs]);

  // Live-tail: poll /api/jobs every 5s while toggled on. Pauses when the tab
  // is hidden so we don't burn cycles in the background.
  useEffect(() => {
    if (!liveTail) return;
    const tick = () => {
      if (document.visibilityState === 'visible') loadJobs();
    };
    liveTailRef.current = setInterval(tick, 5000);
    return () => {
      if (liveTailRef.current) clearInterval(liveTailRef.current);
      liveTailRef.current = null;
    };
  }, [liveTail, loadJobs]);

  const retryJob = async (jobId) => {
    setRetrying(r => ({ ...r, [jobId]: true }));
    try {
      await api.retryJob(jobId);
      toast.success('Job re-queued');
      await loadJobs();
    } catch (err) {
      toast.error(err.message || 'Retry failed');
    } finally {
      setRetrying(r => ({ ...r, [jobId]: false }));
    }
  };

  const counts = jobs.reduce((acc, j) => { acc[j.status] = (acc[j.status] || 0) + 1; return acc; }, {});
  const kinds = Array.from(new Set(jobs.map(j => j.kind))).sort();

  return (
    <div className="p-6 space-y-6" data-testid="admin-jobs-page">
      <div className="flex items-start justify-between gap-4 flex-wrap">
        <div>
          <h1 className="text-2xl font-heading font-bold text-foreground flex items-center gap-2">
            <Briefcase className="w-6 h-6 text-primary" /> Background Jobs
          </h1>
          <p className="text-xs text-muted-foreground mt-1">
            Recent background queue activity for your tenant. Failed jobs can be re-queued with one click.
          </p>
        </div>
        <Button onClick={loadJobs} variant="outline" size="sm" className="rounded-lg" data-testid="refresh-jobs-btn">
          <RefreshCw className={`w-3.5 h-3.5 mr-2 ${loading ? 'animate-spin' : ''}`} /> Refresh
        </Button>
      </div>

      {/* Live-tail toggle */}
      <div className="flex items-center justify-end gap-2 -mb-2" data-testid="live-tail-row">
        <Radio className={`w-3.5 h-3.5 ${liveTail ? 'text-emerald-400 animate-pulse' : 'text-muted-foreground/70'}`} />
        <span className="text-xs text-muted-foreground">Live tail (5s)</span>
        <Switch checked={liveTail} onCheckedChange={setLiveTail} data-testid="live-tail-toggle" />
      </div>

      {/* Status counts */}
      <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
        {['queued', 'running', 'done', 'failed'].map(s => (
          <div key={s} className="glass-card rounded-2xl p-4" data-testid={`job-count-${s}`}>
            <p className="text-[10px] text-muted-foreground uppercase tracking-wide">{s}</p>
            <p className="text-2xl font-heading font-bold text-foreground mt-1">{counts[s] || 0}</p>
          </div>
        ))}
      </div>

      {/* Filters */}
      <div className="flex items-center gap-3 flex-wrap">
        <div className="flex items-center gap-2">
          <span className="text-xs text-muted-foreground">Status</span>
          <Select value={statusFilter} onValueChange={setStatusFilter}>
            <SelectTrigger className="w-[140px] h-8 rounded-lg text-xs" data-testid="status-filter">
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="all">All</SelectItem>
              <SelectItem value="queued">Queued</SelectItem>
              <SelectItem value="running">Running</SelectItem>
              <SelectItem value="done">Done</SelectItem>
              <SelectItem value="failed">Failed</SelectItem>
            </SelectContent>
          </Select>
        </div>
        <div className="flex items-center gap-2">
          <span className="text-xs text-muted-foreground">Kind</span>
          <Select value={kindFilter} onValueChange={setKindFilter}>
            <SelectTrigger className="w-[200px] h-8 rounded-lg text-xs" data-testid="kind-filter">
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="all">All</SelectItem>
              {kinds.map(k => <SelectItem key={k} value={k}>{k}</SelectItem>)}
            </SelectContent>
          </Select>
        </div>
      </div>

      {/* Table */}
      <div className="glass-card rounded-2xl overflow-hidden">
        {loading && jobs.length === 0 ? (
          <div className="p-10 text-center text-muted-foreground text-sm">
            <Loader2 className="w-5 h-5 animate-spin mx-auto mb-2" /> Loading jobs…
          </div>
        ) : jobs.length === 0 ? (
          <div className="p-10 text-center text-muted-foreground text-sm" data-testid="jobs-empty-state">
            No background jobs found for the selected filters.
          </div>
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full text-xs">
              <thead className="bg-muted/30 border-b border-border/30">
                <tr className="text-left">
                  <th className="px-3 py-2 font-medium text-muted-foreground">Job ID</th>
                  <th className="px-3 py-2 font-medium text-muted-foreground">Kind</th>
                  <th className="px-3 py-2 font-medium text-muted-foreground">Status</th>
                  <th className="px-3 py-2 font-medium text-muted-foreground">Attempts</th>
                  <th className="px-3 py-2 font-medium text-muted-foreground">Duration</th>
                  <th className="px-3 py-2 font-medium text-muted-foreground">Created</th>
                  <th className="px-3 py-2 font-medium text-muted-foreground text-right">Actions</th>
                </tr>
              </thead>
              <tbody>
                {jobs.map(job => {
                  const isExpanded = expanded[job.job_id];
                  const canRetry = job.status === 'failed' || job.status === 'done';
                  const drill = getJobDrillTarget(job);
                  return (
                    <React.Fragment key={job.job_id}>
                      <tr
                        className="border-b border-border/20 hover:bg-muted/10 cursor-pointer"
                        onClick={() => setExpanded(e => ({ ...e, [job.job_id]: !isExpanded }))}
                        data-testid={`job-row-${job.job_id}`}
                      >
                        <td className="px-3 py-2 font-mono text-[11px] text-foreground/80">{job.job_id}</td>
                        <td className="px-3 py-2">
                          {drill ? (
                            <button
                              onClick={(e) => { e.stopPropagation(); navigate(drill.to); }}
                              className="text-foreground hover:text-primary inline-flex items-center gap-1 group"
                              title={drill.label}
                              data-testid={`job-kind-link-${job.job_id}`}
                            >
                              {job.kind}
                              <ExternalLink className="w-3 h-3 opacity-0 group-hover:opacity-60 transition-opacity" />
                            </button>
                          ) : (
                            <span className="text-foreground">{job.kind}</span>
                          )}
                        </td>
                        <td className="px-3 py-2"><StatusBadge status={job.status} /></td>
                        <td className="px-3 py-2 text-foreground/80">{job.attempts || 0} / {job.max_attempts || 3}</td>
                        <td className="px-3 py-2 text-foreground/80">{fmtDuration(job.duration_ms)}</td>
                        <td className="px-3 py-2 text-foreground/80 whitespace-nowrap">{fmtTime(job.created_at)}</td>
                        <td className="px-3 py-2 text-right">
                          <div className="inline-flex items-center gap-1.5 justify-end">
                            {drill && (
                              <Button
                                variant="ghost"
                                size="sm"
                                className="h-7 rounded-lg text-[11px] px-2"
                                onClick={(e) => { e.stopPropagation(); navigate(drill.to); }}
                                title={drill.label}
                                data-testid={`view-job-target-${job.job_id}`}
                              >
                                {drill.icon ? <drill.icon className="w-3 h-3 mr-1" /> : <ExternalLink className="w-3 h-3 mr-1" />}
                                View
                              </Button>
                            )}
                            {canRetry ? (
                              <Button
                                variant="outline"
                                size="sm"
                                className="h-7 rounded-lg text-[11px]"
                                disabled={!!retrying[job.job_id]}
                                onClick={(e) => { e.stopPropagation(); retryJob(job.job_id); }}
                                data-testid={`retry-job-${job.job_id}`}
                              >
                                {retrying[job.job_id]
                                  ? <Loader2 className="w-3 h-3 mr-1 animate-spin" />
                                  : <RotateCw className="w-3 h-3 mr-1" />}
                                Retry
                              </Button>
                            ) : (!drill && <span className="text-[10px] text-muted-foreground/80">—</span>)}
                          </div>
                        </td>
                      </tr>
                      {isExpanded && (
                        <tr className="border-b border-border/20 bg-muted/5">
                          <td colSpan={7} className="px-3 py-3" data-testid={`job-details-${job.job_id}`}>
                            <div className="grid grid-cols-1 sm:grid-cols-2 gap-3 text-[11px]">
                              <div>
                                <p className="text-muted-foreground mb-1">Scheduled For</p>
                                <p className="font-mono text-foreground/80">{fmtTime(job.scheduled_for)}</p>
                              </div>
                              <div>
                                <p className="text-muted-foreground mb-1">Leased At</p>
                                <p className="font-mono text-foreground/80">{fmtTime(job.leased_at)}</p>
                              </div>
                              {job.error && (
                                <div className="sm:col-span-2">
                                  <p className="text-rose-400 mb-1 flex items-center gap-1"><AlertTriangle className="w-3 h-3" /> Error</p>
                                  <pre className="font-mono text-rose-300/80 bg-rose-500/5 border border-rose-500/20 rounded-lg p-2 whitespace-pre-wrap break-all">{job.error}</pre>
                                </div>
                              )}
                              {job.payload && Object.keys(job.payload).length > 0 && (
                                <div className="sm:col-span-2">
                                  <p className="text-muted-foreground mb-1">Payload</p>
                                  <pre className="font-mono text-foreground/70 bg-muted/30 border border-border/30 rounded-lg p-2 whitespace-pre-wrap break-all">{JSON.stringify(job.payload, null, 2)}</pre>
                                </div>
                              )}
                            </div>
                          </td>
                        </tr>
                      )}
                    </React.Fragment>
                  );
                })}
              </tbody>
            </table>
          </div>
        )}
      </div>
    </div>
  );
}
